import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from "@mui/material";
import { ResponsiveRow } from "../../constants/layout.constants";
import {
  isNullUndefined,
  tryParseFloat,
  tryParseInt,
} from "../../util/util";
import { issueWarnMessage } from "../../actions/message";

export function defaultLatePenalty() {
  return {
    enabled: false,
    graceDays: 0,
    penaltyType: "FIXED",
    fixedAmount: "",
    percentage: "",
    frequency: "ONCE",
  };
}

/** Map legacy DAILY/WEEKLY/MONTHLY to REF_PERIOD codes. */
function normalizeFrequency(raw) {
  switch (raw) {
    case "DAY":
    case "DAILY":
      return "DAY";
    case "WEEK":
    case "WEEKLY":
      return "WEEK";
    case "MNTH":
    case "MONTHLY":
      return "MNTH";
    case "ONCE":
      return "ONCE";
    default:
      return "ONCE";
  }
}

export function normalizeLatePenalty(raw) {
  if (isNullUndefined(raw)) {
    return defaultLatePenalty();
  }
  return {
    enabled: !!raw.enabled,
    graceDays: tryParseInt(raw.graceDays, 0),
    penaltyType: raw.penaltyType === "PCT" ? "PCT" : "FIXED",
    fixedAmount:
      raw.fixedAmount === null || raw.fixedAmount === undefined
        ? raw.amount === null || raw.amount === undefined
          ? ""
          : raw.amount
        : raw.fixedAmount,
    percentage:
      raw.percentage === null || raw.percentage === undefined
        ? ""
        : raw.percentage,
    frequency: normalizeFrequency(raw.frequency),
  };
}

/** Returns false and warns if invalid when enabled. */
export function validateLatePenalty(latePenalty) {
  const cfg = normalizeLatePenalty(latePenalty);
  if (!cfg.enabled) {
    return true;
  }
  if (cfg.graceDays < 0) {
    issueWarnMessage("Grace period cannot be negative.");
    return false;
  }
  if (cfg.penaltyType === "PCT") {
    const pct = tryParseFloat(cfg.percentage, -1);
    if (pct <= 0) {
      issueWarnMessage("Enter a late penalty percentage greater than zero.");
      return false;
    }
  } else {
    const amt = tryParseFloat(cfg.fixedAmount, -1);
    if (amt <= 0) {
      issueWarnMessage("Enter a late penalty fixed amount greater than zero.");
      return false;
    }
  }
  if (
    cfg.frequency !== "ONCE" &&
    cfg.frequency !== "DAY" &&
    cfg.frequency !== "WEEK" &&
    cfg.frequency !== "MNTH"
  ) {
    issueWarnMessage("Select how often the late penalty should apply.");
    return false;
  }
  return true;
}

/**
 * Facility late payment penalty section (property modal).
 *
 * Props: value (object), onChange(nextObject)
 */
export default function LatePenaltyConfig({ value, onChange }) {
  const cfg = normalizeLatePenalty(value);

  function patch(partial) {
    onChange({ ...cfg, ...partial });
  }

  return (
    <ResponsiveRow
      sx={{
        height: "auto",
        width: "100%",
        marginTop: "10px",
        flexDirection: "column",
      }}
    >
      <FormControl sx={{ width: "100%", marginBottom: "10px" }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={cfg.enabled}
              onChange={(e) => patch({ enabled: e.target.checked })}
            />
          }
          label="Enable late payment penalty"
        />
      </FormControl>

      {cfg.enabled && (
        <ResponsiveRow sx={{ width: "100%", height: "auto" }}>
          <FormControl
            sx={{
              width: { xs: "100%", lg: "33.3%" },
              marginBottom: "10px",
            }}
          >
            <TextField
              label="Grace period (days)"
              type="number"
              value={cfg.graceDays}
              onChange={(e) =>
                patch({ graceDays: tryParseInt(e.target.value, 0) })
              }
              inputProps={{ min: 0 }}
              sx={{ width: { xs: "100%", lg: "90%" } }}
            />
          </FormControl>

          <FormControl
            sx={{
              width: { xs: "100%", lg: "33.3%" },
              marginBottom: "10px",
            }}
          >
            <FormLabel>Penalty type</FormLabel>
            <RadioGroup
              row
              value={cfg.penaltyType}
              onChange={(e) => patch({ penaltyType: e.target.value })}
            >
              <FormControlLabel
                value="FIXED"
                control={<Radio />}
                label="Fixed amount"
              />
              <FormControlLabel
                value="PCT"
                control={<Radio />}
                label="Percentage"
              />
            </RadioGroup>
          </FormControl>

          {cfg.penaltyType === "FIXED" ? (
            <FormControl
              sx={{
                width: { xs: "100%", lg: "33.3%" },
                marginBottom: "10px",
              }}
            >
              <TextField
                label="Fixed penalty amount (KES)"
                type="number"
                value={cfg.fixedAmount}
                onChange={(e) => patch({ fixedAmount: e.target.value })}
                sx={{ width: { xs: "100%", lg: "90%" } }}
              />
            </FormControl>
          ) : (
            <FormControl
              sx={{
                width: { xs: "100%", lg: "33.3%" },
                marginBottom: "10px",
              }}
            >
              <TextField
                label="Penalty percentage (%)"
                type="number"
                value={cfg.percentage}
                onChange={(e) => patch({ percentage: e.target.value })}
                sx={{ width: { xs: "100%", lg: "90%" } }}
              />
            </FormControl>
          )}

          <FormControl
            sx={{
              width: { xs: "100%", lg: "33.3%" },
              marginBottom: "10px",
            }}
          >
            <InputLabel>Application frequency</InputLabel>
            <Select
              label="Application frequency"
              input={<OutlinedInput label="Application frequency" />}
              value={cfg.frequency}
              onChange={(e) => patch({ frequency: e.target.value })}
              sx={{ width: { xs: "100%", lg: "90%" } }}
            >
              <MenuItem value="ONCE">One-time</MenuItem>
              <MenuItem value="DAY">Daily</MenuItem>
              <MenuItem value="WEEK">Weekly</MenuItem>
              <MenuItem value="MNTH">Monthly</MenuItem>
            </Select>
          </FormControl>
        </ResponsiveRow>
      )}
    </ResponsiveRow>
  );
}
